import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment, amortizationByYear } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";
import { STATES } from "@/data/states";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function MortgagePage() {
    const [homePrice, setHomePrice] = useState(450000);
    const [downPct, setDownPct] = useState(20);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(30);
    const [propertyTax, setPropertyTax] = useState(1.1);
    const [insurance, setInsurance] = useState(1500);

    const { monthly, totals, schedule, downAmt, loan } = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const downAmt = (hp * dp) / 100;
        const loan = Math.max(0, hp - downAmt);
        const pi = monthlyPayment(loan, r, y);
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const monthly = pi + tax + ins;
        const totalPaid = pi * y * 12;
        const totalInterest = totalPaid - loan;
        const schedule = amortizationByYear(loan, r, y);
        return {
            monthly,
            downAmt,
            loan,
            totals: { totalPaid, totalInterest, pi, tax, ins },
            schedule,
        };
    }, [homePrice, downPct, rate, years, propertyTax, insurance]);

    return (
        <CalculatorShell
            slug="mortgage"
            explainer={
        <>
            <h2>How this mortgage calculator works</h2>
            <p>
                A mortgage calculator estimates your monthly payment based on loan amount, interest
                rate, and term. Use it before applying to set a realistic budget, or to compare what
                different down payments do to your monthly obligation.
            </p>
            <h3>How it works</h3>
            <ul>
                <li><strong>Principal &amp; interest formula:</strong> M = P[r(1+r)ⁿ]/[(1+r)ⁿ−1] — the standard amortization equation behind every fixed-rate payment.</li>
                <li><strong>Down payment:</strong> reduces your principal directly, lowering both monthly payment and total interest paid.</li>
                <li><strong>Property tax &amp; insurance:</strong> added on top of principal and interest — the calculator shows each component separately.</li>
                <li><strong>Amortization:</strong> early payments are mostly interest; principal paydown accelerates over time.</li>
                <li><strong>Total interest paid:</strong> shown across the full term so you can see the true cost of the loan.</li>
            </ul>
            <h3>Tips</h3>
            <ul>
                <li>Put 20% or more down to avoid private mortgage insurance (PMI).</li>
                <li>One extra payment per year can shave years off your term and save thousands in interest.</li>
                <li>Compare a 15-year vs. 30-year side by side — the monthly difference is often smaller than people expect.</li>
            </ul>
        </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Home price" prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} dataTestId="calc-input-down" />
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                        <Field label="Property tax" suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.1} dataTestId="calc-input-tax" />
                    </div>
                    <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                    <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                        <span>Down: {fmtUSD(downAmt)}</span>
                        <span>Loan: {fmtUSD(loan)}</span>
                    </div>
                </div>
                <div className="lg:col-span-2 space-y-6">
                    <ResultStat
                        label="Monthly payment"
                        value={monthly}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "P&I", value: totals.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                            { label: "Tax + Ins", value: totals.tax + totals.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                            { label: "Total paid", value: totals.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                            { label: "Total interest", value: totals.totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                        ]}
                    />
                </div>
            </div>

            <div className="mt-10 bg-card border border-border rounded-xl p-6">
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Amortization</div>
                        <h3 className="font-serif text-2xl text-primary mt-1">Balance over time</h3>
                    </div>
                </div>
                <figure aria-label={`Mortgage balance over ${schedule.length} years, starting at ${fmtUSD(loan)} and ending at zero`}>
                    <div className="h-64 sm:h-80 w-full" data-testid="calc-chart" style={{ minHeight: 256 }}>
                        <ResponsiveContainer width="100%" height="100%" minHeight={256}>
                            <AreaChart data={schedule}>
                                <defs>
                                    <linearGradient id="bal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="hsl(150 55% 9%)" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="hsl(150 55% 9%)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 22% 86%)" vertical={false} />
                                <XAxis dataKey="year" stroke="hsl(150 12% 38%)" tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="hsl(150 12% 38%)" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip
                                    contentStyle={{ background: "white", border: "1px solid #E5E0D5", borderRadius: 8, fontFamily: "IBM Plex Mono" }}
                                    formatter={(v) => fmtUSD(v, { decimals: 0 })}
                                />
                                <Area type="monotone" dataKey="balance" stroke="hsl(150 55% 9%)" fill="url(#bal)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    <figcaption className="sr-only">
                        <table>
                            <caption>Yearly remaining mortgage balance</caption>
                            <thead><tr><th scope="col">Year</th><th scope="col">Balance</th></tr></thead>
                            <tbody>
                                {schedule.filter((_, i) => i % 5 === 0 || i === schedule.length - 1).map((r) => (
                                    <tr key={r.year}><td>{r.year}</td><td>{fmtUSD(r.balance, { decimals: 0 })}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </figcaption>
                </figure>
            </div>

            <section className="mt-12 bg-card border border-border rounded-xl p-6 sm:p-8" data-testid="states-directory">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">By state</div>
                <h3 className="font-serif text-2xl sm:text-3xl text-primary mt-1 mb-2 leading-tight">
                    Mortgage calculators for all 50 US states
                </h3>
                <p className="text-sm text-foreground/70 max-w-2xl leading-relaxed mb-6">
                    Each version is pre-filled with the local effective property tax rate and
                    median home price — tap through to see a state-specific estimate.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {STATES.map((s) => (
                        <Link
                            key={s.code}
                            to={`/calculator/mortgage/${s.code}`}
                            data-testid={`state-link-${s.code}`}
                            className="group flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border hover:border-primary/30 hover:bg-muted/40 transition-all text-sm"
                        >
                            <span className="text-primary group-hover:text-primary">{s.name}</span>
                            <span className="text-[11px] text-muted-foreground font-mono tabular">{s.propertyTax.toFixed(2)}%</span>
                        </Link>
                    ))}
                </div>
            </section>
        </CalculatorShell>
    );
}
